import React from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { 
  Map, 
  List, 
  Lock, 
  ArrowDown, 
  Mail, 
  Clock, 
  Instagram
} from "lucide-react";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import FeatureHighlight from "@/components/onboarding/FeatureHighlight";
import PricingCards from "@/components/onboarding/PricingCards";
import EmailSequence from "@/components/onboarding/EmailSequence";
import FeedbackCard from "@/components/onboarding/FeedbackCard";

const OperatorOnboarding: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Operator Onboarding | SaunaDip</title>
        <meta name="description" content="Join SaunaDip as an operator and grow your sauna or cold plunge business" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-sauna-beige to-white">
        {/* Warm Hero Section for Become Operator */}
        <div className="bg-sauna-orange/10 py-20 px-4 border-b border-sauna-orange/20">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-sauna-navy mb-4">Become a SaunaDip Operator</h1>
            <p className="text-xl text-sauna-navy/80 mb-8">
              Share your sauna or cold plunge business with thousands of wellness seekers. Unlock premium features, boost your visibility, and join a supportive community of operators.
            </p>
            <Button asChild className="bg-sauna-orange hover:bg-sauna-orange/90 text-white text-lg px-8 py-4 rounded-xl shadow-lg font-bold">
              <Link to="/signup/operator">Get Started</Link>
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid gap-16">
            {/* 1. MVP Directory Website */}
            <OnboardingSection 
              id="mvp-directory"
              number={1}
              title="MVP Directory Website"
              subtitle="Your business, discoverable by wellness seekers"
            >
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-lg mb-6 text-gray-700">
                    Our public directory puts your sauna or cold plunge experience in front of motivated customers, with both map and list views for easy discovery.
                  </p>
                  <div className="space-y-4">
                    <FeatureHighlight 
                      icon={<Map />}
                      title="Map + List Views"
                      description="Customers can discover your business through interactive maps or browse detailed listings."
                    />
                    <FeatureHighlight 
                      icon={<List />}
                      title="Professional Profile"
                      description="Showcase your services with photos, descriptions, pricing and contact information."
                    />
                    <FeatureHighlight 
                      icon={<Lock />}
                      title="Premium Features"
                      description="Some features are locked until you claim your listing - see who's viewing your profile and more."
                    />
                  </div>
                  <div className="mt-8">
                    <Button asChild className="bg-sauna-blue hover:bg-sauna-blue/90">
                      <Link to="/directory">
                        Preview Directory
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className="rounded-lg overflow-hidden shadow-lg border border-sauna-blue/10">
                  <div className="bg-sauna-navy text-white p-3 text-center text-sm font-medium">
                    Current Live Directory Preview
                  </div>
                  
                  <div className="relative">
                    <img 
                      src="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&w=800" 
                      alt="Directory Screenshot" 
                      className="w-full" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                      <div>
                        <Badge className="bg-sauna-orange mb-2">Public View</Badge>
                        <h3 className="text-white text-xl font-bold mb-2">Customers are looking for you</h3>
                        <p className="text-white/90 text-sm">
                          Our directory already has search traffic from eager sauna-goers
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </OnboardingSection>

            {/* 2. Operator Teaser Dashboard */}
            <OnboardingSection 
              id="teaser-dashboard"
              number={2}
              title="Operator Teaser Dashboard"
              subtitle="Early insights to grow your business"
            >
              <div className="grid md:grid-cols-2 gap-8 md:items-center">
                <div className="order-2 md:order-1">
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                    <div className="bg-sauna-navy p-4 text-white">
                      <h3 className="font-bold">Atlantic Saunas Dashboard</h3>
                      <p className="text-white/80 text-sm">Your listing is already generating interest</p>
                    </div>
                    
                    <div className="p-6">
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-sauna-beige/50 p-4 rounded-lg border border-sauna-blue/10">
                          <p className="text-sm text-gray-500">Profile Views</p>
                          <p className="text-2xl font-bold text-sauna-navy">243</p>
                          <p className="text-xs text-sauna-green">↑ 12% this week</p>
                        </div>
                        
                        <div className="bg-sauna-beige/50 p-4 rounded-lg border border-sauna-blue/10">
                          <p className="text-sm text-gray-500">Ranking</p>
                          <p className="text-2xl font-bold text-sauna-navy">#3</p>
                          <p className="text-xs">in Kerry County</p>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                        <div className="flex justify-between mb-2">
                          <p className="font-medium">New Messages</p>
                          <Lock size={16} className="text-sauna-orange" />
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <div className="w-6 h-6 rounded-full bg-sauna-blue/20 flex items-center justify-center">
                            1
                          </div>
                          <div className="blur-sm">customer@example.com tried to contact...</div>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                        <div className="flex justify-between mb-2">
                          <p className="font-medium">Incomplete Profile</p>
                          <Lock size={16} className="text-sauna-orange" />
                        </div>
                        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                          <div className="bg-sauna-orange h-full" style={{ width: "65%" }}></div>
                        </div>
                        <p className="text-xs mt-2 text-gray-500">Add photos to increase visibility</p>
                      </div>
                      
                      <Button className="w-full bg-sauna-orange hover:bg-sauna-orange/90">
                        Claim Your Listing
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="order-1 md:order-2">
                  <h3 className="text-2xl font-bold text-sauna-navy mb-4">
                    Auto-Generated Listing Preview
                  </h3>
                  <p className="text-lg mb-6 text-gray-700">
                    We've already created a basic profile for your business from public data. Claim it to unlock:
                  </p>
                  <div className="space-y-4">
                    <FeatureHighlight 
                      icon={<ArrowDown />}
                      title="Real-Time Analytics"
                      description="See exactly how many potential customers are viewing your profile."
                    />
                    <FeatureHighlight 
                      icon={<Mail />}
                      title="Direct Messages"
                      description="Receive booking inquiries directly through the platform."
                    />
                    <FeatureHighlight 
                      icon={<Clock />}
                      title="14-Day Premium Trial"
                      description="No credit card required to try all premium features."
                    />
                  </div>
                </div>
              </div>
            </OnboardingSection>

            {/* 3. Stripe Setup with Trial */}
            <OnboardingSection 
              id="pricing"
              number={3}
              title="Simple Pricing & Free Trial"
              subtitle="Start with no commitment, upgrade when you're ready"
            >
              <PricingCards />
            </OnboardingSection>

            {/* 4. Welcome Email Sequence */}
            <OnboardingSection 
              id="emails"
              number={4}
              title="Welcome Email Sequence"
              subtitle="We'll guide you every step of the way"
            >
              <EmailSequence />
            </OnboardingSection>

            {/* 5 & 6. Admin Digest & Marketing */}
            <div className="grid md:grid-cols-2 gap-8">
              <OnboardingSection 
                id="admin-digest"
                number={5}
                title="Admin Digest"
                subtitle="We're hands-on with our operators"
                className="h-full"
              >
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-full bg-sauna-navy flex items-center justify-center text-white">
                        <Mail size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold">Weekly Operator Support</h4>
                        <p className="text-sm text-gray-500">Direct line to our team</p>
                      </div>
                    </div>
                    
                    <p className="mb-4 text-gray-700">
                      Our team reviews all operator accounts weekly and provides personalized support to help you succeed:
                    </p>
                    
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start gap-2">
                        <div className="min-w-4 pt-1">•</div>
                        <p className="text-sm">Personalized suggestions to improve your listing</p>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="min-w-4 pt-1">•</div>
                        <p className="text-sm">Updates on new platform features</p>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="min-w-4 pt-1">•</div>
                        <p className="text-sm">Insights into customer behavior</p>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="min-w-4 pt-1">•</div>
                        <p className="text-sm">Advance notice of marketing opportunities</p>
                      </li>
                    </ul>
                    
                    <div className="mt-6 text-center">
                      <Badge className="bg-sauna-blue text-white">First Launch Benefit</Badge>
                    </div>
                  </CardContent>
                </Card>
              </OnboardingSection>
              
              <OnboardingSection 
                id="marketing"
                number={6}
                title="Instagram & Email Marketing"
                subtitle="We promote you to our audience"
                className="h-full"
              >
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-600 to-orange-500 flex items-center justify-center text-white">
                        <Instagram size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold">Social & Email Promotion</h4>
                        <p className="text-sm text-gray-500">Reach our engaged audience</p>
                      </div>
                    </div>
                    
                    <Tabs defaultValue="instagram" className="w-full">
                      <TabsList className="mb-4 w-full">
                        <TabsTrigger value="instagram" className="flex-1">Instagram</TabsTrigger>
                        <TabsTrigger value="email" className="flex-1">Email</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="instagram">
                        <div className="relative border border-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src="https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&w=500"
                            alt="Instagram post preview"
                            className="w-full h-48 object-cover"
                          />
                          <div className="p-3">
                            <p className="font-medium text-sm">Featured: Atlantic Saunas</p>
                            <p className="text-xs text-gray-500">Experience the perfect blend of heat and sea at Ireland's premier mobile sauna...</p>
                          </div>
                          <div className="absolute top-2 right-2">
                            <Badge className="bg-white/80 text-black text-xs px-2">Instagram Post</Badge>
                          </div>
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="email">
                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Mail size={16} className="text-sauna-blue" />
                            <p className="text-sm font-medium">Weekly Digest</p>
                          </div>
                          <h5 className="text-lg font-medium mb-2">New Sauna Experience in Kerry</h5>
                          <p className="text-sm text-gray-600 mb-4">Discover this week's featured sauna experience at Inch Beach...</p>
                          <Button variant="outline" size="sm" className="text-xs">View in Newsletter</Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                    
                    <div className="mt-4 text-sm text-gray-600">
                      <p>Premium operators receive featured placement in our marketing channels, reaching sauna enthusiasts across Ireland & UK.</p>
                    </div>
                  </CardContent>
                </Card>
              </OnboardingSection>
            </div>

            {/* 7. Feedback Loop */}
            <OnboardingSection 
              id="feedback"
              number={7}
              title="Feedback Loop"
              subtitle="Help us build the platform you need"
            >
              <FeedbackCard />
            </OnboardingSection>

            {/* Final CTA */}
            <div className="mt-8 text-center">
              <h2 className="text-3xl font-bold mb-6 text-sauna-navy">Ready to join the SaunaDip community?</h2>
              <Button asChild className="bg-sauna-orange hover:bg-sauna-orange/90 text-white text-lg px-8 py-6">
                <Link to="/signup/operator">Add Your Business Now</Link>
              </Button>
              <p className="mt-4 text-gray-500">No commitment required to get started</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OperatorOnboarding;
